import React from 'react';
import { Grid, Box } from '@mui/material';
import InfoButton from 'src/App/Dashboards/Market/Components/Buttons/InfoButton';
import AirbnbBadge from './AirbnbBadge';
import IntelligenceSection from './IntelligenceSection';
import VerifiedMarketSection from './VerifiedMarketSection';
import BookingPatternCharts, { type BookingPatternChartsProps } from './BookingPatternCharts';
import PropertySizeCharts from './PropertySizeCharts';
import ListingsPerformanceCharts from './ListingsPerformanceCharts';
import DistributionCharts from './DistributionCharts';
import InternationDomesticGuestsCard from './InternationDomesticGuestsCard';
import TopCitiesCard from './TopCitiesCard';
import TopCountriesCard from './TopCountriesCard';
import GuestLanguageCard from './GuestLanguageCard';
import Hosts from './Hosts';
import HotelSupplyAndAvailabilityCard from './HotelSupplyAndAvailabilityCard';
import ExtraPricingCard from './ExtraPricingCard';
import DailyOccupancyRateCalendar from 'src/App/Dashboards/Market/Components/DailyOccupancyRateCalendar/DailyOccupancyRateCalendar';
import { LoadingCard } from 'src/App/Dashboards/Market/Components/Cards';
import { Search } from 'src/interfaces/idashboard';

export interface MarketIntelligenceTabProps {
  marketDisplayName?: string;
  isMobileView?: boolean;
  handleOnSampleClick?: (plan: string, source?: string) => void;
  cachedMarketData?: any;
  topHosts?: any;
  currency?: string;
  user?: any;
  matches?: boolean;
  searchObj?: Search | null;
  isUserAllowed?: boolean;
  warningTextComponent?: any;
  setSelectedGraphExportDetails?: any;
  domesticVsInternationalGraphRef?: React.RefObject<any>;
  noDataInMarket?: boolean;
  userTailorMarketAccess?: boolean;
  isDisabled?: boolean;
  dashboardLayoutView?: string;
  isDemoMarket?: boolean;
  dailyOccupancyRateCalendarHistoricalGraphRef?: React.RefObject<any>;
  dailyOccupancyRateCalendarFutureGraphRef?: React.RefObject<any>;
  calendarMarketDisplayName?: string;
  calendarMarketId?: string | number;
  /** Rendered above the "Others" section when provided (e.g. Short-Term Rental Property Characteristics). */
  shortTermRentalSection?: React.ReactNode;
  /** When true, show market-wide badge on sections that use market-wide data. */
  filterApplied?: boolean;
  /** Original market cache from getMarketDataCacheTest (unfiltered). Used e.g. for Property Size so it does not change with filters. */
  cachedMarketDataWithoutFilters?: any;
  /** Called when user clicks CTA to trigger hard re-cache (listings only); caller schedules refetches at 10s, 20s, 30s. */
  onRequestHardRefresh?: () => void;
}

const MARKET_INTELLIGENCE_SECTIONS: Array<{
  title: string;
  summary: string;
  hidden?: boolean;
  requiresVerifiedMarket?: boolean;
}> = [
  {
    title: 'Booking Pattern Analysis',
    summary:
      'Booking patterns by *day*, *day of week*, *week*, *future pacing*, and *stay length*.',
    requiresVerifiedMarket: true,
  },
  {
    title: 'Guests Analysis',
    summary:
      "Who's staying in Airbnbs: *domestic vs international*, *top cities and countries*, *guest language*.",
  },
  {
    title: 'Property Size Analysis',
    summary:
      '*ADR*, *occupancy*, *revenue*, and *seasonality* by bedroom count.',
  },
  {
    title: 'Distribution Analysis',
    summary:
      'Occupancy, *ADR*, *revenue*, and *active days* distributions.',
    hidden: true,
  },
  {
    title: 'Listings Performance',
    summary:
      '*Occupancy*, *ADR*, *revenue*, and *listing availability* distributions in this market.',
  },
  {
    title: 'Hotel Supply & Availability',
    summary:
      'Hotel supply and availability from *Booking.com* compared with short-term rental supply in this market.',
    requiresVerifiedMarket: true,
  },
  {
    title: 'Others',
    summary:
      '*Cleaning fee* and *extra guest fee* typical values in this market.',
  },
  {
    title: 'Top Airbnb Management Companies',
    summary:
      'Top management companies by listing count (T1–3 verified markets only).',
    requiresVerifiedMarket: true,
  },
];

const MarketIntelligenceTab: React.FC<MarketIntelligenceTabProps> = ({
  marketDisplayName,
  isMobileView = false,
  handleOnSampleClick = () => {},
  cachedMarketData,
  topHosts,
  currency = 'USD',
  user,
  matches = false,
  searchObj,
  isUserAllowed = false,
  warningTextComponent,
  setSelectedGraphExportDetails,
  domesticVsInternationalGraphRef,
  noDataInMarket = false,
  userTailorMarketAccess = false,
  isDisabled = false,
  dashboardLayoutView = 'dashboard',
  isDemoMarket = false,
  dailyOccupancyRateCalendarHistoricalGraphRef,
  dailyOccupancyRateCalendarFutureGraphRef,
  calendarMarketDisplayName,
  calendarMarketId,
  shortTermRentalSection,
  filterApplied = false,
  cachedMarketDataWithoutFilters,
  onRequestHardRefresh,
}) => {
  const handleGuestClick = (reqPlan: string) => handleOnSampleClick(reqPlan, 'guestOriginGraphs');

  const guestsContent = (
    <Box sx={{ width: '100%' }}>
      <Box className="subsection--spacing">
        {cachedMarketData?.loading ? (
          <LoadingCard height={350} type="bars" />
        ) : (
          <InternationDomesticGuestsCard
            graph={cachedMarketData}
            isDisabled={isDisabled}
            handleOnSampleClick={handleGuestClick}
            userTailorMarketAccess={userTailorMarketAccess}
            warningTextComponent={warningTextComponent}
            domesticVsInternationalGraphRef={domesticVsInternationalGraphRef}
            setSelectedGraphExportDetails={setSelectedGraphExportDetails}
          />
        )}
      </Box>
      <Box className="subsection--spacing" sx={{ mt: 2 }}>
        <Grid container direction="row" spacing={1} justifyContent="flex-start" alignItems="flex-start">
          <Grid item xs={matches ? 12 : 4}>
            {cachedMarketData?.loading ? (
              <LoadingCard height={150} type="bars" />
            ) : (
              <TopCitiesCard
                graph={cachedMarketData}
                isDisabled={isDisabled}
                handleOnSampleClick={handleGuestClick}
                matches={matches}
                userTailorMarketAccess={userTailorMarketAccess}
                warningTextComponent={warningTextComponent}
                setSelectedGraphExportDetails={setSelectedGraphExportDetails}
                noDataInMarket={noDataInMarket}
              />
            )}
          </Grid>
          <Grid item xs={matches ? 12 : 4}>
            {cachedMarketData?.loading ? (
              <LoadingCard height={150} type="bars" />
            ) : (
              <TopCountriesCard
                graph={cachedMarketData}
                isDisabled={isDisabled}
                handleOnSampleClick={handleGuestClick}
                matches={matches}
                userTailorMarketAccess={userTailorMarketAccess}
                tooltipSuffix=" of total guests"
                warningTextComponent={warningTextComponent}
                setSelectedGraphExportDetails={setSelectedGraphExportDetails}
              />
            )}
          </Grid>
          <Grid item xs={matches ? 12 : 4}>
            {cachedMarketData?.loading ? (
              <LoadingCard height={150} type="bars" />
            ) : (
              <GuestLanguageCard
                graph={cachedMarketData}
                isDisabled={isDisabled}
                handleOnSampleClick={handleGuestClick}
                matches={matches}
                userTailorMarketAccess={userTailorMarketAccess}
                tooltipSuffix=" of total guests"
                warningTextComponent={warningTextComponent}
                setSelectedGraphExportDetails={setSelectedGraphExportDetails}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  const hostsProps = {
    topHosts,
    currency,
    handleOnSampleClick: (reqPlan: string) => handleOnSampleClick(reqPlan, 'hostsTable'),
    user,
    matches,
    dashboardLayoutView: (dashboardLayoutView === 'marketIntelligence' ? 'dashboard' : dashboardLayoutView) as 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing',
    userTailorMarketAccess,
    searchObj: searchObj!,
    isUserAllowed,
    cachedMarketData,
    warningTextComponent,
  };

  const getSectionChildren = (sectionTitle: string) => {
    switch (sectionTitle) {
      case 'Booking Pattern Analysis': {
        const bookingProps: BookingPatternChartsProps = {
          isMobileView,
          useMarketIntelligenceLayout: true,
          cachedMarketData,
          isUserAllowed,
        };
        const calendarDisplayName = calendarMarketDisplayName ?? marketDisplayName ?? '';
        const calendarId = calendarMarketId ?? searchObj?.id;
        return (
          <>
            <BookingPatternCharts {...bookingProps} />
            {calendarId != null && calendarId !== '' && (
              <Box sx={{ mt: 3 }}>
                <DailyOccupancyRateCalendar
                  user={user!}
                  marketDisplayName={calendarDisplayName}
                  handleOnSampleClick={handleOnSampleClick}
                  isMobileScreen={matches}
                  isDemoMarket={isDemoMarket}
                  warningTextComponent={warningTextComponent}
                  dailyOccupancyRateCalendarHistoricalGraphRef={
                    dailyOccupancyRateCalendarHistoricalGraphRef!
                  }
                  dailyOccupancyRateCalendarFutureGraphRef={
                    dailyOccupancyRateCalendarFutureGraphRef!
                  }
                  setSelectedGraphExportDetails={setSelectedGraphExportDetails!}
                  marketId={String(calendarId)}
                  autoReveal={true}
                />
              </Box>
            )}
          </>
        );
      }
      case 'Guests Analysis':
        return guestsContent;
      case 'Property Size Analysis': {
        // Always use original unfiltered cache so this section does not change with filters
        const propertySizeCache =
          cachedMarketDataWithoutFilters != null
            ? { data: cachedMarketDataWithoutFilters, loading: false }
            : { data: cachedMarketData?.data, loading: cachedMarketData?.loading ?? true };
        return (
          <PropertySizeCharts
            isMobileView={isMobileView}
            useMarketIntelligenceLayout={true}
            cachedMarketData={propertySizeCache}
            isUserAllowed={isUserAllowed}
            handleOnSampleClick={handleOnSampleClick}
          />
        );
      }
      case 'Distribution Analysis':
        return <DistributionCharts isMobileView={isMobileView} cachedMarketData={cachedMarketData} />;
      case 'Listings Performance': {
        const listingsPerfCache =
          cachedMarketDataWithoutFilters != null
            ? { data: cachedMarketDataWithoutFilters, loading: false }
            : { data: cachedMarketData?.data, loading: cachedMarketData?.loading ?? true };
        return (
          <ListingsPerformanceCharts
            isMobileView={isMobileView}
            cachedMarketData={listingsPerfCache}
            currency={currency}
            onRequestHardRefresh={onRequestHardRefresh}
            isUserAllowed={isUserAllowed}
          />
        );
      }
      case 'Hotel Supply & Availability':
        return (
          <HotelSupplyAndAvailabilityCard
            isMobileView={isMobileView}
            marketId={searchObj?.id}
            isVerifiedMarket={(searchObj as Search & { isVerifiedMarket?: boolean })?.isVerifiedMarket ?? false}
          />
        );
      case 'Others':
        return (
          <ExtraPricingCard
            currency={currency}
            cachedData={cachedMarketData?.data?.extra_pricing ?? null}
            viewMode="dashboard"
            isDynamicLoading={false}
            multipleMarkersCallLoading={cachedMarketData?.loading ?? false}
            noDataInMarket={noDataInMarket}
          />
        );
      case 'Top Airbnb Management Companies':
        return topHosts && searchObj ? <Hosts {...hostsProps} /> : null;
      default:
        return undefined;
    }
  };

  const isVerifiedMarket = (searchObj as Search & { isVerifiedMarket?: boolean })?.isVerifiedMarket ?? false;

  const getSectionDisplayTitle = (title: string) => {
    if (title === 'Booking Pattern Analysis')
      return `When Is Airbnb Demand Highest in ${marketDisplayName ?? 'this market'}?`;
    if (title === 'Guests Analysis') return `Who Visits ${marketDisplayName ?? 'this market'}?`;
    if (title === 'Property Size Analysis') return 'Which Property Size Performs Best?';
    if (title === 'Listings Performance') return `How Do Listings Perform in ${marketDisplayName ?? 'this market'}?`;
    if (title === 'Hotel Supply & Availability') return 'How Do Hotels Compare to Short-Term Rentals?';
    if (title === 'Others') return 'Extra Pricing';
    return title;
  };

  return (
    <Grid container item xs={12} sx={{ width: '100%' }}>
      {MARKET_INTELLIGENCE_SECTIONS.filter(
        (section) => !section.hidden
      ).map((section) => (
        <React.Fragment key={section.title}>
          {section.title === 'Others' && shortTermRentalSection && (
            <Grid item xs={12} className="section--spacing">
              {shortTermRentalSection}
            </Grid>
          )}
          <Grid item xs={12}>
            {section.requiresVerifiedMarket ? (
              <VerifiedMarketSection
                title={getSectionDisplayTitle(section.title)}
                titleAdornment={
                  section.title === 'Hotel Supply & Availability' ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.25,
                        py: 0.375,
                        borderRadius: 10,
                        backgroundColor: '#004090',
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'default',
                      }}
                    >
                      Booking.com
                    </Box>
                  ) : section.title === 'Booking Pattern Analysis' ? (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, verticalAlign: 'middle' }}>
                      <InfoButton
                        title={getSectionDisplayTitle(section.title)}
                        tooltipContent="The market occupancy rate shown at the top of the dashboard uses the median across listings, while these charts show average occupancy by day and week."
                        extraStyles={{ marginLeft: 0 }}
                      />
                    </Box>
                  ) : undefined
                }
                summary={undefined}
                isVerifiedMarket={isVerifiedMarket}
                isMobileView={isMobileView}
                handleOnSampleClick={handleOnSampleClick}
                showMarketWideBadge={true}
                filterApplied={filterApplied}
              >
                {getSectionChildren(section.title)}
              </VerifiedMarketSection>
            ) : (
              <IntelligenceSection
                title={getSectionDisplayTitle(section.title)}
                titleAdornment={
                  section.title === 'Guests Analysis' ? (
                    <AirbnbBadge tooltipTitle="Guest origin data (domestic vs international, top cities and countries) is based on Airbnb supply in this market." />
                  ) : undefined
                }
                summary={undefined}
                isMobileView={isMobileView}
                handleOnSampleClick={handleOnSampleClick}
                showMarketWideBadge={
                  section.title === 'Guests Analysis' ||
                  section.title === 'Property Size Analysis' ||
                  section.title === 'Listings Performance'
                }
                showVerifiedLockBadge={false}
                filterApplied={filterApplied}
                isVerifiedMarket={isVerifiedMarket}
                children={getSectionChildren(section.title)}
              />
            )}
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  );
};

export default MarketIntelligenceTab;
